import { supabase } from './client';
import type { GolfCourseApiSearchResult } from '@/types/golfCourseApi';

export interface CachedCourseResult {
  api_course_id: number;
  club_name: string;
  course_name: string;
  city: string;
  state: string;
  country: string;
  similarity: number;
}

/**
 * Search the local course cache using pg_trgm fuzzy matching.
 */
export async function searchCourseCache(
  query: string,
  limit = 20,
  minSimilarity = 0.15,
): Promise<CachedCourseResult[]> {
  const { data, error } = await supabase.rpc('search_course_cache', {
    search_query: query,
    result_limit: limit,
    min_similarity: minSimilarity,
  });

  if (error) {
    console.error('searchCourseCache error:', error);
    return [];
  }

  return (data as CachedCourseResult[]) ?? [];
}

/**
 * Fire-and-forget upsert of API search results into the cache.
 */
export function upsertCourseCache(results: GolfCourseApiSearchResult[]): void {
  if (results.length === 0) return;

  const entries = results.map(r => ({
    api_course_id: r.id,
    club_name: r.club_name ?? '',
    course_name: r.course_name ?? '',
    city: r.location?.city ?? '',
    state: r.location?.state ?? '',
    country: r.location?.country ?? '',
  }));

  supabase
    .rpc('upsert_course_cache', { entries })
    .then(({ error }) => {
      if (error) console.error('upsertCourseCache error:', error);
    });
}

/**
 * Convert a cached result to the GolfCourseApiSearchResult shape
 * so downstream components work unchanged.
 */
export function cachedToApiSearchResult(cached: CachedCourseResult): GolfCourseApiSearchResult {
  return {
    id: cached.api_course_id,
    club_name: cached.club_name,
    course_name: cached.course_name,
    location: {
      city: cached.city,
      state: cached.state,
      country: cached.country,
    },
    tees: {},
  };
}
